import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServer } from '@adornis/bh-util/util.js';
import { sendCertificateToParticipant } from '@adornis/buchungsheld-db/api/certificate.js';
import {
  AppointmentParticipationForBulkUpdate,
  setAppointmentParticipationBulk,
  setIkhDoNotSendInvoiceBulk,
  setParticipationRequirementBulk,
} from '@adornis/buchungsheld-db/api/participation.js';
import type { ActionItem, ParticipationOverviewPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import type { ParticipationEinzelberatung } from '@adornis/buchungsheld-db/entities/ParticipationEinzelberatung.js';
import {
  AppointmentParticipation,
  ParticipationWorkshop,
} from '@adornis/buchungsheld-db/entities/ParticipationWorkshop.js';
import { Workshop } from '@adornis/buchungsheld-db/entities/workshop.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import { Permission, Role } from '@adornis/buchungsheld-db/enums/roles.js';
import { translations } from '@adornis/buchungsheld-db/translations/translation.js';
import { ParticipationDataview } from '@adornis/buchungsheld-db/use-case-entities/ParticipationsDataview.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import type { BHParticipationDataview } from '@adornis/buchungsheld-dependent-components/bh-participation-dataview.js';
import { bhPromptConfirmParticipationRemove } from '@adornis/buchungsheld-theme/prompts/bh-prompt-confirm.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { obs } from '@adornis/chemistry/directives/observable.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';

const _XNativeDialog = () => import('@adornis/dialog/x-native-dialog.js').then(m => m.XNativeDialog);

import { FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import { getCountryByCode } from '@adornis/internationalization/helpers/flag.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { Language } from '@adornis/translation-core/languages.js';
import { Translator } from '@adornis/translation-core/translation.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { firstValueFrom, map } from 'rxjs';

const translator = new Translator({
  dictionary: translations,
  languages: [Language.GERMAN, Language.ENGLISH],
  getSelectedLanguage: () => Language.GERMAN,
  selectLanguage: () => {},
});

export function defaultGetDropdownActions(
  host: (typeof BHParticipationDataview)['prototype'],
  targetDataList: ParticipationDataview[],
  paymentActive: boolean,
): ActionItem[] {
  if (!targetDataList.length) return [];

  const firstData = targetDataList[0]!;
  const mergableAttr = {
    stateSet: !!targetDataList.filter(data => data.buyer?.email).length,
    manualPaymentReceived: targetDataList.every(
      data => data.has('payment') && data.payment?.status && data.payment.status !== 'paid',
    ),
  };

  const action_toggleInvoices = () => {
    if (!paymentActive || host.mode === 'lecturer') return null;
    const fulfilledCount = targetDataList.filter(data => !data.participation.ikh_doNotSendInvoice).length;
    const checked: ActionItem['checked'] =
      fulfilledCount === targetDataList.length ? true : fulfilledCount === 0 ? false : 'mixed';
    return {
      checked,
      action: async () => {
        const target = checked !== true;
        try {
          const ids = targetDataList.map(d => d.participation._id);
          await setIkhDoNotSendInvoiceBulk(ids, !target)();
          for (const { participation } of targetDataList) {
            participation.ikh_doNotSendInvoice = !target;
            if (typeof (participation as any).commit === 'function') await participation.commit(true);
          }
        } catch (err) {
          await host.e(err);
        } finally {
          host.refreshSelectedUserData();
        }
      },
      icon: 'receipt_long',
      text: 'Rechnung senden',
    };
  };

  const action_toggleAppointmentParticipation = () => {
    const product = host.product.value as Maybe<Workshop>;
    if (!product || !(product instanceof Workshop)) return [];
    const currentUser = host._currentUser.value;
    if (
      host.mode === 'lecturer' ||
      !currentUser?.roles ||
      !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, product.institutionID)
    )
      return [];
    const appointments = product.appointments;
    const participation = firstData.participation;
    if (!(participation instanceof ParticipationWorkshop)) return [];
    return appointments.map((appointment, i) => {
      const appointmentName = appointment.name || 'Termin ' + (i + 1);
      const fulfilledCount = targetDataList.filter(
        data =>
          data.participation instanceof ParticipationWorkshop &&
          data.participation.appointmentParticipations?.some(it => it.appointmentID === appointment._id),
      ).length;
      const checked: ActionItem['checked'] =
        fulfilledCount === targetDataList.length ? true : fulfilledCount === 0 ? false : 'mixed';
      return {
        icon: 'check_circle',
        text: `${appointmentName} teilgenommen`,
        checked,
        action: async () => {
          const target = checked !== true;
          try {
            await setAppointmentParticipationBulk(
              targetDataList.map(
                data =>
                  new AppointmentParticipationForBulkUpdate({
                    participationID: data.participation._id,
                    appointmentID: appointment._id,
                    fullfilled: target,
                  }),
              ),
            )();
            for (const { participation } of targetDataList) {
              if (!(participation instanceof ParticipationWorkshop)) continue;
              participation.appointmentParticipations ??= [];
              if (target) {
                if (!participation.appointmentParticipations.some(it => it.appointmentID === appointment._id)) {
                  participation.appointmentParticipations.push(
                    new AppointmentParticipation({ appointmentID: appointment._id }),
                  );
                }
              } else {
                participation.appointmentParticipations = participation.appointmentParticipations.filter(
                  it => it.appointmentID !== appointment._id,
                );
              }
              if (typeof (participation as any).commit === 'function') await participation.commit(true);
            }
          } catch (err) {
            await host.e(err);
          } finally {
            host.refreshSelectedUserData();
          }
        },
      };
    });
  };

  const action_toggleParticipationRequirements = () => {
    const product = host.product.value;
    const currentUser = host._currentUser.value;
    if (
      !product ||
      host.mode === 'lecturer' ||
      !currentUser?.roles ||
      !currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, product.institutionID)
    )
      return [];
    const constraints = product.settings.extraParticipationConstraints;
    if (!constraints) return [];

    return constraints.map(constraint => {
      const fulfilledCount = targetDataList.filter(data =>
        data.participation.fulfilledParticipationRequirements?.includes(constraint),
      ).length;
      const checked: ActionItem['checked'] =
        fulfilledCount === targetDataList.length ? true : fulfilledCount === 0 ? false : 'mixed';
      return {
        icon: 'check_circle',
        text: constraint,
        checked,
        action: async () => {
          const target = checked !== true;
          try {
            const ids = targetDataList.map(d => d.participation._id);
            await setParticipationRequirementBulk(ids, constraint, target)();
            for (const { participation } of targetDataList) {
              participation.fulfilledParticipationRequirements ??= [];
              if (target) {
                if (!participation.fulfilledParticipationRequirements.includes(constraint)) {
                  participation.fulfilledParticipationRequirements.push(constraint);
                }
              } else {
                participation.fulfilledParticipationRequirements =
                  participation.fulfilledParticipationRequirements.filter(it => it !== constraint);
              }
              if (typeof (participation as any).commit === 'function') await participation.commit(true);
            }
          } catch (err) {
            await host.e(err);
          } finally {
            host.refreshSelectedUserData();
          }
        },
      };
    });
  };

  const action_togglePaymentStatus = () => {
    if (!mergableAttr.manualPaymentReceived) return null;
    return {
      action: async () => {
        if (await (await _XNativeDialog()).confirm(host._translation.translate('PAYMENT', 'update_status' as any))) {
          await (
            await _XNativeDialog()
          ).waitFor(
            (async () => {
              for (const targetData of targetDataList) {
                targetData.payment!.status = 'paid';
              }
              host.clearUserSelection();
            })().catch(host.e),
          );
        }
      },
      icon: 'credit_card',
      text: host._translation.translate('PAYMENT', 'mark_as_paid' as any),
    };
  };

  const action_sendCertificate = () => {
    const allParticipationsAreMarkedAsParticipated = targetDataList.every(
      data => data.participation.state === ParticipationState.participated,
    );

    if (!allParticipationsAreMarkedAsParticipated) return [];

    return [
      {
        action: async () => {
          const sendITs = targetDataList
            .map(data => data.participation._id)
            .map(id => sendCertificateToParticipant(id)());

          const onSuccess = async () => {
            host.clearUserSelection();
            const { XSnackbar } = await import('@adornis/chemistry/elements/components/x-snackbar.js');
            void XSnackbar.show(host._translation.translate('workshop', 'successfully_sent_certificates' as any));
          };

          await (await _XNativeDialog()).waitFor(Promise.all(sendITs)).then(onSuccess).catch(host.e);
        },
        icon: 'send',
        text: host._translation.translate('workshop', 'send_certificate' as any),
      },
    ];
  };

  return [
    action_toggleInvoices(),
    ...action_toggleAppointmentParticipation(),
    ...action_toggleParticipationRequirements(),
    action_togglePaymentStatus(),
    ...action_sendCertificate(),
  ].filter(isDefined);
}

type SelectionSet = { [field: string]: 1 | SelectionSet | undefined };

function mergeSelectionSets(left: SelectionSet, right: SelectionSet): SelectionSet {
  const merged = { ...left };
  for (const [field, selection] of Object.entries(right)) {
    const existing = merged[field];
    if (typeof selection === 'object')
      merged[field] = mergeSelectionSets(typeof existing === 'object' ? existing : {}, selection);
    else if (selection === 1) merged[field] = 1;
  }
  return merged;
}

export const defaultParticipationOverviewPlugin: ParticipationOverviewPlugin = {
  id: 'participation-overview::default',
  type: 'participation-overview',
  getDropdownItemActions: defaultGetDropdownActions,
  getColumnDefinitions: (
    host,
    product,
    coloring,
    paymentActive,
    participationState,
    extraColumns,
    operation,
    filter,
    suspensionHandler,
    deSuspensionHandler,
  ) => {
    //find out if user is lecturer and not admin
    const currentUser = host._currentUser.value;
    const callerIsLecturer =
      currentUser?.roles?.some(role => role.name === Role.LECTURER) &&
      !currentUser?.roles?.some(role => role.name === Role.ADMIN || role.name === Role.SUPER_ADMIN);

    const customColumns: ColumnDefinition<typeof ParticipationDataview>[] = [
      {
        columnWidth: 'max-content',
        customID: 'selection-handle',
        renderHeader: tableView => {
          if (!tableView) return nothing;
          host.registerTableView(tableView);
          return html`
            ${obs(
              host._selectedUsers.observable.pipe(
                map(
                  () => html`
                    <x-flex center crossaxis-center ${css({ padding: `0 ${host.spacing.sm}` })}>
                      ${obs(
                        host._selectedUsers.observable.pipe(
                          map(
                            () => html`
                              <tr-checkbox
                                outlined
                                boolean-only
                                enableicon="adjust"
                                disableicon
                                .coloring=${coloring}
                                @click=${(e: MouseEvent) => e.stopPropagation()}
                                @value-picked=${async (e: ValueEvent<boolean>) => {
                                  const selectAllSelectionSet = selectionSet(() => ParticipationDataview, 2);
                                  const fullSelectionSet = mergeSelectionSets(
                                    tableView.fullOptions.value!.finalSelectionSet(),
                                    {
                                      ...selectAllSelectionSet,
                                      participation: {
                                        ...selectAllSelectionSet.participation,
                                        ikh_doNotSendInvoice: 1,
                                        fulfilledParticipationRequirements: 1,
                                        appointmentParticipations: { _id: 1, appointmentID: 1 },
                                      },
                                    },
                                  );
                                  const allData = await (
                                    await _XNativeDialog()
                                  ).waitFor(firstValueFrom(operation(0, 10000, filter)(fullSelectionSet)));

                                  tableView.data.push(...allData.slice(tableView.data.length));

                                  if (e.detail.value === true)
                                    host._selectedUsers.next({
                                      ...host._selectedUsers.value,
                                      [participationState]: allData.map(data => ParticipationDataview.resolveID(data)),
                                    });
                                  if (e.detail.value === false)
                                    host._selectedUsers.next({
                                      ...host._selectedUsers.value,
                                      [participationState]: [],
                                    });
                                }}
                                .value=${
                                  tableView.data.length &&
                                  host._selectedUsers.value[participationState].length === tableView.data.length
                                }
                              ></tr-checkbox>
                            `,
                          ),
                        ),
                      )}
                    </x-flex>
                  `,
                ),
              ),
            )}
          `;
        },
        renderItem: (entityInstance, viewElement) => {
          const idsCtrl = host._selectedUsers;
          const curID = ParticipationDataview.resolveID(entityInstance);

          return html`
            <tr-flex center crossaxis-center ${css({ margin: `0 ${host.spacing.xs}` })}>
              ${obs(
                host._selectedUsers.observable.pipe(
                  map((allSelectedUsers: Record<ParticipationState, string[]>) => {
                    const isSelected = Object.values(allSelectedUsers).some(ids => ids?.includes(curID));
                    return html`
                      <tr-checkbox
                        outlined
                        boolean-only
                        enableIcon="adjust"
                        disableIcon=""
                        .coloring=${coloring}
                        .value=${isSelected}
                        @value-picked=${(e: ValueEvent<boolean>) => {
                          const allSelectedUsers: Record<ParticipationState, string[]> = idsCtrl.value;
                          const selectedUsers = allSelectedUsers[participationState] ?? [];
                          if (e.detail.value === false)
                            idsCtrl.next(
                              Object.fromEntries(
                                Object.entries(allSelectedUsers).map(([state, ids]) => [
                                  state,
                                  ids.filter(id => id !== curID),
                                ]),
                              ) as Record<ParticipationState, string[]>,
                            );
                          if (e.detail.value === true)
                            idsCtrl.next({
                              ...allSelectedUsers,
                              [participationState]: Object.values(allSelectedUsers).some(ids => ids?.includes(curID))
                                ? selectedUsers
                                : selectedUsers.concat([curID]),
                            });
                        }}
                      >
                        ${isSelected ? 'radio_button_checked' : 'radio_button_unchecked'}
                      </tr-checkbox>
                    `;
                  }),
                ),
              )}
            </tr-flex>
          `;
        },
      },

      ...(extraColumns ?? []).filter(
        col => col.fieldName !== 'payment.status' && col.fieldName !== 'buyer.whatsappNotification',
      ),
      ...(host.showWhatsappNotification
        ? [
            {
              fieldName: 'buyer.whatsappNotification',
              columnName: host._translation.translate('field', 'whatsappNotification'),
              selectionSet: { buyer: { whatsappNotification: 1 as const } },
              getFieldMapping: (entity: ParticipationDataview) =>
                entity.buyer?.whatsappNotification ? 'Ja' : 'Nein',
              renderItem: (entity: ParticipationDataview) => html`<x-text>
                ${host._translation.translate(entity.buyer?.whatsappNotification ? 'yes' : 'no')}
              </x-text>`,
            },
          ]
        : []),
      ...(!callerIsLecturer && (extraColumns ?? []).some(col => col.fieldName === 'payment.status')
        ? [
            {
              columnName: host._translation.translate('PAYMENT', 'payment') as string,
              fieldName: 'payment.status',
              renderItem: instance => html`
                <x-text>
                  ${(() => {
                    if (!instance.payment?.status) return '-';
                    return host._translation.translate('PAYMENT', 'STATE', instance.payment.status as any);
                  })()}
                </x-text>
              `,
            },
          ]
        : []),
      ...(!callerIsLecturer && product instanceof Workshop
        ? product.appointments?.map((appointment, i) => {
            const appointmentName = appointment.name ?? 'Termin ' + (i + 1);
            return {
              customID: '_appointment_participation_' + appointment._id,
              columnWidth: `CALC( ${host.spacing.lg} + ${host.spacing.xxs} )`,
              columnName: appointmentName + '?',
              selectionSet: (<const>{
                product: { appointments: { name: 1 } },
                participation: { appointmentParticipations: { appointmentID: 1 }, ikh_doNotSendInvoice: 1 },
              }) as any, // TODO: fix this
              renderHeader: _ =>
                host._renderColHeader('check_circle', appointmentName + '?', { margin: `0 -3px 0 3px` }),
              renderItem: instance => {
                const fullfilled = !!instance.participation.appointmentParticipations?.find(
                  ap => ap.appointmentID === appointment._id,
                );
                return html`
                  <x-icon
                    ${css({
                      fontSize: host.spacing.md,
                      fontWeight: fullfilled ? 'bold' : 'unset',
                      color: fullfilled ? host.colors.tone.success : host.colors.tone.error,
                    })}
                  >
                    ${fullfilled ? 'check' : 'close'}
                  </x-icon>
                `;
              },
            };
          })
        : []),
      ...(callerIsLecturer
        ? []
        : (product.settings.extraParticipationConstraints?.map((constraint, i) => {
            return {
              customID: '_extra_participation_constraint_' + i,
              columnWidth: `CALC( ${host.spacing.lg} + ${host.spacing.xxs} )`,
              columnName: constraint + '?',
              selectionSet: <const>{ participation: { fulfilledParticipationRequirements: 1 } },
              renderHeader: _ => host._renderColHeader('check_circle', constraint + '?', { margin: `0 -3px 0 3px` }),
              renderItem: instance => {
                const fullfilled = instance.participation.fulfilledParticipationRequirements?.includes(constraint);
                return html`
                  <x-icon
                    ${css({
                      fontSize: host.spacing.md,
                      fontWeight: fullfilled ? 'bold' : 'unset',
                      color: fullfilled ? host.colors.tone.success : host.colors.tone.error,
                    })}
                  >
                    ${fullfilled ? 'check' : 'close'}
                  </x-icon>
                `;
              },
            };
          }) ?? [])),
      ...(product instanceof Einzelberatung
        ? [
            {
              columnName: 'Berater',
              fieldName: 'berater.email',
              renderItem: (instance: ParticipationDataview) => {
                return html` <tr-text> ${instance.berater?.email} </tr-text> `;
              },
            },
            {
              columnName: 'Zeitslot',
              fieldName: 'participation.timeslotID',
              renderItem: (instance: ParticipationDataview) => {
                const slot = product.slots.find(
                  slot => slot._id === (instance.participation as ParticipationEinzelberatung).timeslotID,
                );
                return html`
                  <tr-text>
                    ${slot?.start ? format(slot.start, 'dd.LL.yyyy') : ''} <br />
                    ${slot?.start ? format(slot.start, 'HH:mm') : ''} - ${slot?.end ? format(slot.end, 'HH:mm') : ''}
                  </tr-text>
                `;
              },
            },
          ]
        : []),
      ...byAdminColumn(host),
      ...(paymentActive && !callerIsLecturer
        ? [
            {
              columnName: 'erhält Rechnung',
              fieldName: 'participation.ikh_doNotSendInvoice',
              columnWidth: `CALC( ${host.spacing.lg} + ${host.spacing.xxs} )`,
              renderHeader: _ => host._renderColHeader('receipt_long', 'erhält Rechnung?'),
              renderItem: instance => html`
                <x-icon
                  ${css({
                    fontSize: host.spacing.md,
                    fontWeight: !instance.participation.ikh_doNotSendInvoice ? 'bold' : 'unset',
                    color: !instance.participation.ikh_doNotSendInvoice
                      ? host.colors.tone.success
                      : host.colors.tone.error,
                  })}
                >
                  ${!instance.participation.ikh_doNotSendInvoice ? 'check' : 'close'}
                </x-icon>
              `,
            },
          ]
        : []),
      {
        columnWidth: 'max-content',
        customID: '_options',
        columnName: 'Aktion',
        renderHeader: _ => host._renderColHeader('electric_bolt', 'Teilnahmestatus anpassen'),
        selectionSet: <const>{ participation: { state: 1, userID: 1 } },
        renderItem: instance => {
          return html`
            <x-flex horizontal space-between center crossaxis-center space="xs">
              <tr-button-small
                icon="arrow_right"
                mode="outline"
                .action=${() =>
                  goTo(
                    `/admin/institution-details/${product.institutionID}/users/userDetail/${instance.participation.userID}`,
                  )}
              ></tr-button-small>

              ${
                currentUser &&
                currentUser.roles && // this check is needed because otherwise 'hasPermission' failes sometimes}
                currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, product.institutionID)
                  ? html`
                      <tr-button-small
                        icon="block"
                        .color=${
                          instance.participation.state === ParticipationState.suspended
                            ? host.colors.tone.success
                            : host.colors.tone.error
                        }
                        .action=${async e => {
                          e.preventDefault();
                          e.stopPropagation();
                          const isSuspended = instance.participation.state === ParticipationState.suspended;
                          const userID = instance.participation.userID;
                          const institutionID = product.institutionID;
                          if (isSuspended) deSuspensionHandler(userID, institutionID);
                          else suspensionHandler(userID, institutionID, [instance.participation._id]);
                        }}
                      ></tr-button-small>
                    `
                  : nothing
              }
              ${
                currentUser &&
                currentUser.roles && // this check is needed because otherwise 'hasPermission' failes sometimes
                currentUser.hasPermission(Permission.MANAGE_PARTICIPATION, product.institutionID)
                  ? html`
                      <tr-button-small
                        icon="delete"
                        .color=${host.colors.tone.error}
                        .action=${async () =>
                          bhPromptConfirmParticipationRemove(async () => await instance.participation.remove())}
                      ></tr-button-small>
                    `
                  : nothing
              }
            </x-flex>
          `;
        },
      },
    ];

    return customColumns;
  },

  getCSVFieldsForExport: async stammdatenFields => {
    const currentUserInfo = await getCurrentUserServer();

    //if lecturer, but not admin, then show only first name, lastname and country of origin
    const callerIsLecturer = currentUserInfo?.roles?.some(role => role.name === Role.LECTURER);
    const isAdmin = currentUserInfo?.roles?.some(role => role.name === Role.ADMIN || role.name === Role.SUPER_ADMIN);

    return callerIsLecturer && !isAdmin
      ? [
          //non-admins can only export limited user data
          { input: 'Vorname', output: 'user.firstName' },
          { input: 'Nachname', output: 'user.lastName' },
          { input: 'Status', output: 'participation.state' },
        ]
      : [
          { input: 'ID', output: 'participation._id' },
          { input: 'Vorname', output: 'user.firstName' },
          { input: 'Nachname', output: 'user.lastName' },
          { input: 'E-Mail', output: 'user.email' },
          { input: 'Status', output: 'participation.state' },
          new FieldMappingEntry({ input: 'Wartelistenplatz' }, undefined, (v: any, data: any, i: number) => {
            if (data.participation.state !== ParticipationState.waiting) return '-';
            return data.participation?.waitingListPlace ?? '-';
          }),
          new FieldMappingEntry({ input: 'Letzte Änderung' }, undefined, (v: any, data: any, i: number) =>
            data.participation?.updatedAt ? format(data.participation.updatedAt, 'dd.LL.yyyy') : '',
          ),
          ...stammdatenFields.map(field => {
            return new FieldMappingEntry({ input: field }, undefined, (v: any, data: any, i: number) => {
              const value = data.stammdaten?.[field];
              if (value == null) return '';
              const fieldType = data.stammdaten?.fields[field]?.type();
              if (fieldType === Boolean) return value ? 'ja' : 'nein';
              if ((fieldType as any)?._isBaseQLEnum) return translator.translate('enums', value);
              if (['nationality', 'countryOfBirth'].includes(field)) return getCountryByCode(value, Language.GERMAN);
              if (value instanceof Date) return format(value, 'PP');
              return value;
            });
          }),
          /* settings */
          { input: 'Einstellungen ID', output: 'settings._id' },
          { input: 'Kurs Ort', output: 'settings.courseLocation' },
          { input: '1. E-Mail Erinnerung', output: 'settings.firstReminderLeadTime' },
          { input: '2. E-Mail Erinnerung', output: 'settings.secondReminderLeadTime' },
          { input: 'Abmeldezeitraum', output: 'settings.blockSignOffDaysInAdvance' },
          { input: 'Nicht Aufrücken Zeitraum', output: 'settings.blockAutomaticMoveUpDaysInAdvance' },
          { input: 'QR-Code Gültigkeit Dauer (Minuten)', output: 'settings.qrCodeValidityInMinutes' },
        ];
  },
};

// columns
function byAdminColumn(host: (typeof BHParticipationDataview)['prototype']) {
  return [
    {
      columnWidth: 'max-content',
      renderHeader: _ => host._renderColHeader('gavel', 'Nutzer wurde durch einen Admin in diesen Status gebracht'),
      fieldName: 'participation.lastStateChangeWasInitiatedByAdmin',
      columnName: 'durch Admin',
      selectionSet: selectionSet(() => ParticipationDataview, 2),
    },
  ];
}
